export type TimelinePlaybackPerfEvent =
  | { type: 'playback-start'; playheadTicks: number; speed: number }
  | { type: 'playback-stop'; playheadTicks: number }
  | { type: 'clock-tick'; playheadTicks: number; speed: number }
  | {
      type: 'playback-ui-update'
      channel: 'store-playhead' | 'local-playhead-visual' | 'playhead-follow' | 'awareness-intent'
      playheadTicks?: number
      reason?: string
      count?: number
    }
  | { type: 'video-load'; assetId: string; loadMs: number }
  | { type: 'sink-ready'; assetId: string; waitMs: number; wasWarm: boolean; pipelineId?: string }
  | { type: 'sink-error'; assetId: string; waitMs: number; wasWarm: boolean; reason: string; pipelineId?: string }
  | {
      type: 'sink-pipeline-stage'
      assetId: string
      pipelineId?: string
      stage: string
      elapsedMs: number
      cacheSource?: string
      isPrewarming?: boolean
      pathKind?: string
      ok?: boolean
      reason?: string
    }
  | {
      type: 'sink-url-resolved'
      assetId: string
      pipelineId?: string
      elapsedMs: number
      cacheSource: string
      backgroundCacheStatus?: string
      checkIndexedDB: boolean
      offlineMode: boolean
      blobUrlReplaced?: boolean
      cachePolicy?: string
      sourceKey?: string
      cacheKey?: string
      storageIdentity?: string | null
      indexedDBMetadataStatus?: string
      indexedDBMetadataMs?: number
      indexedDBBlobReadMs?: number
      indexedDBSkippedReason?: string
      requestedProxyResolution?: string | null
      cachedProxyResolution?: string | null
      cacheIdentityMismatchReason?: string
      pathKind: string
    }
  | {
      type: 'url-source-fetch'
      assetId: string
      pipelineId?: string
      requestId: string
      elapsedMs: number
      status: number | null
      ok: boolean
      requestRange: string | null
      requestedBytes?: number | null
      retryCount?: number
      requestKind: string
      responseKind: string
      pathKind: string
      headers: Record<string, { state: string; value?: string }>
      errorName?: string
    }
  | {
      type: 'video-cache-background'
      assetId: string
      cacheId: string
      action: string
      elapsedMs?: number
      stage?: string
      reason?: string
      status?: number
      bytes?: number
      pathKind?: string
    }
  | {
      type: 'background-work'
      kind: string
      action: string
      count?: number
      reason?: string
    }
  | {
      type: 'cache-warm-coverage'
      windowStartSeconds: number
      windowDurationSeconds: number
      total: number
      exact: number
      stale: number
      miss: number
      error: number
      fillStatuses?: Record<string, number>
      requestedProxyResolutions?: Record<string, number>
      cachedProxyResolutions?: Record<string, number>
      mismatchReasons?: Record<string, number>
      resolutionMismatches?: Record<string, number>
      networkAdaptationEnabled?: string | null
      previewQuality?: string | null
      elapsedMs?: number
    }
  | {
      type: 'render-queue-state'
      action: string
      assetId: string | null
      requestId?: string
      supersededRequestId?: string
      sourceTimeSeconds?: number
      supersededSourceTimeSeconds?: number
      inFlightDecodes: number
      latestRequestedSourceTimeSeconds?: number | null
      /** Cumulative count of playback requests skipped via current-frame reuse. */
      schedulerReuseCount?: number
      /** Cumulative count of samples acquired via the sequential samples() iterator. */
      schedulerSequentialCount?: number
      /** Cumulative count of samples acquired via random `getSample()`. */
      schedulerRandomCount?: number
    }
  | {
      type: 'initial-frame-decoded'
      mediaTimeSeconds: number
      sampleMs?: number
      videoFrameMs?: number
      bitmapMs?: number
      workerDecodeMs?: number
      decodeMs: number
    }
  | {
      type: 'frame-request'
      requestId?: string
      playheadTicks: number
      sourceTimeSeconds: number
      assetId: string | null
    }
  | {
      type: 'frame-decoded'
      mediaTimeSeconds: number
      sampleMs?: number
      videoFrameMs?: number
      bitmapMs?: number
      workerDecodeMs?: number
      decodeMs: number
      requestId?: string
    }
  | { type: 'frame-drawn'; mediaTimeSeconds: number; drawMs: number; requestId?: string }
  | { type: 'frame-drop'; mediaTimeSeconds: number; reason: string; requestId?: string }
  | { type: 'clip-switch'; assetId: string | null }
  | { type: 'empty-span'; playheadTicks: number }

type MartiniPerfWindow = Window & {
  __perf?: {
    recordTimelinePlayback?: (event: TimelinePlaybackPerfEvent) => void
    recordCanvasPanelRender?: (renderer: 'v3') => void
  }
}

export function isTimelinePlaybackPerfEnabled(): boolean {
  return process.env.NEXT_PUBLIC_PERF_HOOKS === '1'
}

export function recordTimelinePlaybackPerf(event: TimelinePlaybackPerfEvent): void {
  if (!isTimelinePlaybackPerfEnabled()) return
  if (typeof window === 'undefined') return
  ;(window as MartiniPerfWindow).__perf?.recordTimelinePlayback?.(event)
}

/** Benchmark-only render counter; the build flag compiles this out of ordinary builds. */
export function recordCanvasPanelRenderPerf(renderer: 'v3'): void {
  if (process.env.NEXT_PUBLIC_PERF_HOOKS !== '1') return
  if (typeof window === 'undefined') return
  ;(window as MartiniPerfWindow).__perf?.recordCanvasPanelRender?.(renderer)
}
